import { ScoreSource } from '@/types/restaurant';
import { tasteMatchBadgeCopy } from '@/copy/tasteMatchBadge';

/**
 * Minimal restaurant data needed for rating/compatibility functions.
 * This allows functions to work with both full Restaurant and RestaurantSearchResult types.
 */
export interface RatableRestaurant {
  rating?: number | null;
  /**
   * Public/Google-derived rating. This is the real, live runtime field name
   * — `google_rating` on the backend (RestaurantSummary / restaurant models
   * / serializers / recommendation payloads), camelCased to `googleRating`
   * by apiClient's response interceptor. Keep this in sync with the
   * `Restaurant` type (src/types/restaurant.ts); a mismatch here silently
   * disables the entire public-rating fallback chain (this was a confirmed
   * live bug from a prior mis-rename to `publicRating`, see paire-app#1).
   */
  googleRating?: number | null;
  isBookmarked?: boolean;
  /**
   * D41a tier-router contract (optional here so functions accepting this
   * minimal interface still work against callers that haven't threaded the
   * field through). Undefined/null is a real, expected state (stale cache,
   * endpoint not yet emitting it) — every reader of this field must fall
   * back to the pre-D41a `rating`/`googleRating`-based binary logic in that
   * case, not crash or silently misrender. See `Restaurant.scoreSource`
   * (src/types/restaurant.ts) for the full enum doc.
   */
  scoreSource?: ScoreSource | null;
  /** NEVER render as member-facing text. See `Restaurant.rankingTierReason`. */
  rankingTierReason?: string | null;
  /** -1.0..1.0, `embedding` tier only. See `Restaurant.affinityScore`. Never rendered. */
  affinityScore?: number | null;
}

/**
 * "This restaurant HAS a personal TasteMatch rating" (paire-app#1 follow-up
 * refactor, review round 2). Consolidates what was previously inlined as
 * `restaurant.rating == null` / `!= null` checks across the file into one
 * predicate — and, importantly, points in the POSITIVE direction.
 *
 * Why direction matters: a TypeScript type predicate over a plain
 * (non-union) interface only narrows reliably in the branch where the
 * predicate is asserted true — including the early-return/negated form
 * (`if (!hasPersonalRating(r)) return; // r.rating is number here`), because
 * TS can compute "the declared type minus the predicate's complement" for
 * that direction. The mirror-image predicate (`restaurant is RatableRestaurant
 * & { rating: null | undefined }`, i.e. what `hasNoPersonalRating` used to
 * assert directly) does NOT get equivalent treatment in ITS negated branch —
 * `restaurant.rating` stays typed `number | null | undefined` there, forcing
 * an `as number` cast at every "has a rating" call site. Defining the
 * predicate in the positive ("has a rating") direction and deriving the
 * negative one from it (see `hasNoPersonalRating` below) sidesteps that
 * entirely, in both the if-true and if-false forms.
 *
 * `0` is a valid rating and MUST NOT be treated as "no rating" — hence
 * `!= null` (which only excludes `null`/`undefined`), never a truthiness
 * check (`!restaurant.rating`, which would incorrectly exclude `0`).
 *
 * Used directly by `getCompatibilityColor`, `getCompatibilityEmoji`,
 * `getCardCompatibilityIcon`, and `getCompatibilityLevel` below, and
 * indirectly (via `hasNoPersonalRating`) by `getCompatibilityBorderColor`
 * and `getCompatibilityGlow`.
 *
 * `getRatingIconPath` (map pins) uses this same predicate, so a leaked
 * `rating` on a non-personal score source is also "no personal rating". Since
 * 2026-10-04 (CD) it no longer falls back to `googleRating` either — a
 * restaurant with no personal rating gets the grey "Unsure TasteMatch" pin
 * whether or not a public rating exists, matching the card badge's
 * "Unsure TasteMatch" state.
 *
 * D41b Phase 2 addition: `scoreSource` (D41a's tier-router contract, see
 * `Restaurant.scoreSource`) is now consulted as the authoritative signal —
 * `public_consensus`/`embedding`/`none` are always "no personal rating"
 * regardless of `rating`, matching the backend contract (`rating` is
 * guaranteed null for every tier but `self_rating`/`taste_match`) as
 * defense-in-depth against a future data inconsistency. When `scoreSource`
 * itself is missing (stale cache, endpoint not yet emitting the field),
 * this falls back to the original `rating != null` check unchanged — the
 * five card/badge functions that consume this predicate
 * (`getCompatibilityEmoji`, `getCompatibilityColor`, `getCardCompatibilityIcon`,
 * and via `hasNoPersonalRating`: `getCompatibilityBorderColor`,
 * `getCompatibilityGlow`) all inherit this scoreSource-awareness for free,
 * and all five continue to return the exact same "not sure" output for
 * `public_consensus`/`embedding`/`none` alike — this predicate does not
 * vary the badge/corner-glow treatment by tier, only by personal-vs-not
 * (CD has twice rejected varying that specific visual by public-data
 * availability, see restaurantUtils.compatibilityIcon.test.ts).
 */
export const hasPersonalRating = (
  restaurant: RatableRestaurant
): restaurant is RatableRestaurant & { rating: number } => {
  if (
    restaurant.scoreSource != null &&
    restaurant.scoreSource !== 'self_rating' &&
    restaurant.scoreSource !== 'taste_match'
  ) {
    return false;
  }
  return restaurant.rating != null;
};

/**
 * Inverse of `hasPersonalRating` — reads more naturally at early-return call
 * sites ("if there's no personal rating yet, show the grey/neutral state").
 * Defined in terms of the positive predicate rather than its own independent
 * `== null` check so the two can never drift out of sync.
 */
export const hasNoPersonalRating = (
  restaurant: RatableRestaurant
): restaurant is RatableRestaurant & { rating: null | undefined } =>
  !hasPersonalRating(restaurant);

// No data pins
import noDataPin from '@pins-svg/00_noDataPin.svg';

// No compatibility pins (01)
import noCompatPin from '@pins-svg/01_noCompatibilityPin.svg';
import noCompatListPin from '@pins-svg/01_noCompatibilityListPin.svg';

// Low compatibility pins (02)
import lowCompatPin from '@pins-svg/02_lowCompatibilityPin.svg';
import lowCompatListPin from '@pins-svg/02_lowCompatibilityListPin.svg';

// Some compatibility pins (03)
import someCompatPin from '@pins-svg/03_someCompatibilityPin.svg';
import someCompatListPin from '@pins-svg/03_someCompatibilityListPin.svg';

// Medium compatibility pins (04)
import mediumCompatPin from '@pins-svg/04_mediumCompatibilityPin.svg';
import mediumCompatListPin from '@pins-svg/04_mediumCompatibilityListPin.svg';

// High compatibility pins (05)
import highCompatPin from '@pins-svg/05_highCompatibilityPin.svg';
import highCompatListPin from '@pins-svg/05_highCompatibilityListPin.svg';

// Most compatibility pins (06)
import mostCompatPin from '@pins-svg/06_mostCompatibilityPin.svg';
import mostCompatListPin from '@pins-svg/06_mostCompatibilityListPin.svg';

// Import emoji variations
import noCompatibilityEmoji from '@/assets/images/emojis/00_noCompatibilityEmoji.svg';
import lowCompatibilityEmoji from '@/assets/images/emojis/01_lowCompatibilityEmoji.svg';
import someCompatibilityEmoji from '@/assets/images/emojis/02_someCompatibilityEmoji.svg';
import mediumCompatibilityEmoji from '@/assets/images/emojis/03_mediumCompatibilityEmoji.svg';
import highCompatibilityEmoji from '@/assets/images/emojis/04_highCompatibilityEmoji.svg';
import mostCompatibilityEmoji from '@/assets/images/emojis/05_mostCompatibilityEmoji.svg';
import notSureEmoji from '@/assets/images/emojis/06_notSureEmoji.svg';
import { RATING_THRESHOLDS } from './ratingThresholds';
import { SURPRISE_ME_MIN_POOL_SIZE, SURPRISE_ME_MIN_TASTEMATCH_SCORES } from './surpriseMeRules';

// Re-exported so existing `@/utils/restaurantUtils` imports keep working; the
// definitions live in dependency-free modules (see surpriseMeRules.ts).
export { RATING_THRESHOLDS, SURPRISE_ME_MIN_POOL_SIZE, SURPRISE_ME_MIN_TASTEMATCH_SCORES };

const pinAssets: Record<string, string> = {
  // Regular compatibility pins (user ratings)
  '01_noCompatibilityPin': noCompatPin,
  '01_noCompatibilityListPin': noCompatListPin,
  '02_lowCompatibilityPin': lowCompatPin,
  '02_lowCompatibilityListPin': lowCompatListPin,
  '03_someCompatibilityPin': someCompatPin,
  '03_someCompatibilityListPin': someCompatListPin,
  '04_mediumCompatibilityPin': mediumCompatPin,
  '04_mediumCompatibilityListPin': mediumCompatListPin,
  '05_highCompatibilityPin': highCompatPin,
  '05_highCompatibilityListPin': highCompatListPin,
  '06_mostCompatibilityPin': mostCompatPin,
  '06_mostCompatibilityListPin': mostCompatListPin,
};

const emojiAssets: Record<string, string> = {
  '01_no': noCompatibilityEmoji,
  '02_low': lowCompatibilityEmoji,
  '03_some': someCompatibilityEmoji,
  '04_medium': mediumCompatibilityEmoji,
  '05_high': highCompatibilityEmoji,
  '06_most': mostCompatibilityEmoji,
};

// Compatibility level colors - aligned with CSS variables in variables.css
// Note: primaryColor values must remain as hex for runtime color calculations (hexToRgb)
export const COMPATIBILITY_LEVELS = [
  { key: '01_no', primaryLabel: 'No TasteMatch', secondaryLabel: 'None', primaryColor: '#f43636', secondaryColor: 'red.500' },
  { key: '02_low', primaryLabel: 'Low TasteMatch', secondaryLabel: 'Low', primaryColor: '#FFA03E', secondaryColor: 'orange.400' },
  { key: '03_some', primaryLabel: 'Some TasteMatch', secondaryLabel: 'Some', primaryColor: '#fffd57', secondaryColor: 'yellow.500' },
  { key: '04_medium', primaryLabel: 'Medium TasteMatch', secondaryLabel: 'Medium', primaryColor: '#c2ff74', secondaryColor: 'green.300' },
  { key: '05_high', primaryLabel: 'High TasteMatch', secondaryLabel: 'High', primaryColor: '#53fa5b', secondaryColor: 'green.400' },
  { key: '06_most', primaryLabel: 'Highest TasteMatch', secondaryLabel: 'Highest', primaryColor: '#02cf0e', secondaryColor: 'green.600' },
] as const;

export const getCompatibilityLevelFromInternalRating = (rating: number): number => {
  if (rating >= RATING_THRESHOLDS.internal.most) return 5;
  if (rating >= RATING_THRESHOLDS.internal.high) return 4;
  if (rating >= RATING_THRESHOLDS.internal.medium) return 3;
  if (rating >= RATING_THRESHOLDS.internal.some) return 2;
  if (rating >= RATING_THRESHOLDS.internal.low) return 1;
  return 0;
};

const getCompatibilityLevelFromMenuRating = (rating: number): number => {
  switch (rating) {
    case 5: return 5;
    case 4: return 4;
    case 3.5: return 3;
    case 3: return 2;
    case 2: return 1;
    default: return 0;
  }
};

const getEmojiKeyFromLevel = (level: number): string => {
  return COMPATIBILITY_LEVELS[level]?.key || '01_no';
};

const generatePinKey = (emojiKey: string, isListed: boolean = false): string => {
  const listSuffix = isListed ? 'List' : '';
  return `${emojiKey}Compatibility${listSuffix}Pin`;
};

export interface RatingEmojiProps {
  icon: string;
  label: string;
  value: number;
}

export const RATING_EMOJIS: Array<RatingEmojiProps> = [
  {
    icon: noCompatibilityEmoji,
    label: "No",
    value: 1,
  },
  {
    icon: lowCompatibilityEmoji,
    label: "Low",
    value: 2,
  },
  {
    icon: someCompatibilityEmoji,
    label: "Some",
    value: 3,
  },
  {
    icon: mediumCompatibilityEmoji,
    label: "Medium",
    value: 3.5,
  },
  {
    icon: highCompatibilityEmoji,
    label: "High",
    value: 4,
  },
  {
    icon: mostCompatibilityEmoji,
    label: "Highest",
    value: 5,
  },
] as const;

export const getEmojiByRating = (rating: number) =>
  RATING_EMOJIS[Math.max(0, Math.min(rating - 1, RATING_EMOJIS.length - 1))];

/**
 * Maps a `RATING_EMOJIS` value (1, 2, 3, 3.5, 4, 5) to its
 * `COMPATIBILITY_LEVELS` glow/selection color. Shared by every rating-booth
 * emoji button so the emoji-value → level-index mapping lives in one place
 * instead of being reimplemented per component.
 */
export const getRatingGlowColor = (value: number): string => {
  const levelIndex = [1, 2, 3, 3.5, 4, 5].indexOf(value);
  return levelIndex >= 0
    ? COMPATIBILITY_LEVELS[levelIndex].primaryColor
    : COMPATIBILITY_LEVELS[0].primaryColor;
};

/**
 * Converts a recommendation-scale `score` (0-100, the scale
 * RecommendationOrchestrator emits — see paire-api/paire_api/services/
 * recommendation/orchestrator.py:426,487) into an internal-rating-scale
 * equivalent (-100..100, the scale `getCompatibilityLevelFromInternalRating`
 * and friends expect) so a 0-100 score can reuse the same emoji/color/level
 * helpers as personal ratings elsewhere in the app. The current caller is
 * the Digest (`src/lib/digest/scoreConversion.ts`), which bridges its 0-1
 * `score` onto this 0-100 domain first.
 *
 * No existing score -> level/emoji banding convention was found to reuse,
 * and the backend's own orchestrator comments (orchestrator.py:225-237)
 * explicitly document that the recommendation score scales are "NOT
 * unified" with the personal rating (-100..100) scale used here — this is
 * a different, separate scale, not "the same kind of 0-100 match score" as
 * any UI element on the signed internal scale. Never apply this function to
 * an already-internal-scale rating (see `src/utils/tasteMatchBand.ts`,
 * which must call `getCompatibilityLevelFromInternalRating` directly on a
 * raw `rating` field, never through this conversion). Absent a real
 * convention, this is a direct linear
 * proportional mapping of the 0-100 domain onto the -100..100 internal
 * range (0 -> -100, 50 -> 0, 100 -> 100). Reconcile here if a real banding
 * convention is introduced later — don't invent a second one.
 */
export const getInternalRatingFromRecommendationScore = (score: number): number | null => {
  // Guard against undefined/non-finite `score`: `score * 2 - 100` would
  // silently produce NaN, which is NOT caught by the `== null` fallback
  // chains downstream (getCompatibilityEmoji, getRatingIconPath, etc.) and
  // would fall through to level 0 ("None"/worst match) instead of the
  // correct "not sure" no-data state. Returning `null` here routes it
  // through the existing no-personal-rating (`hasPersonalRating`) branches correctly.
  if (!Number.isFinite(score)) return null;
  return score * 2 - 100;
};

// "Not sure" state: no personal rating exists for this restaurant (a public
// rating may or may not exist; it never changes this state). This must be visually distinct from "None"/"Low" TasteMatch
// (which mean we scored it and it's a poor match) — true absence of data is
// not the same thing as the worst possible score.
//
// `NOT_SURE_EMOJI_CHAR` (the literal 🤔 Unicode character) is used separately
// by RestaurantMapView's canvas compositing to draw a face onto the grey
// "Unsure TasteMatch" map pins (and by the Pin Key) — do not remove it even though the
// badge/card icon below no longer needs it inline.
// Defined in the dependency-free copy module so `@/copy` consumers do not pull
// this file's asset imports; re-exported to keep existing importers working.
export { NOT_SURE_EMOJI_CHAR } from '@/copy/tasteMatchBadge';
// Badge/card icon: the real Apple-style thinking-face asset (06_notSureEmoji.svg,
// sourced from the iamcal/emoji-data dataset — see that file's header comment
// for the licensing caveat), matching the other 6 rating-level assets in
// src/assets/images/emojis/. Replaces an earlier inline-generated SVG data URI
// (a plain Unicode-glyph placeholder) now that a real asset exists — see
// paire-app#1.
const notSureEmojiIcon = notSureEmoji;

export const NOT_SURE_RATING_EMOJI: RatingEmojiProps = {
  icon: notSureEmojiIcon,
  // The tier word ("Unsure TasteMatch" once the badge's TasteMatch noun
  // follows it; full label in `tasteMatchBadgeCopy.label`).
  label: tasteMatchBadgeCopy.tierWord,
  value: 0,
};

const gradientColors = [
  ['#D63BCE', '#B57BEE'],
  ['#53FA5B', '#74DEEE'],
  ['#40c9ff', '#e81cff'],
  ['#B739DD', '#EA5459'],
  ['#05C9F9', '#E5F61B'],
  ['#ff1b6b', '#45caff'],
  ['#0061FF', '#60EFFF'],
  ['#ff0f7b', '#f89b29'],
  ['#ff930f', '#fff95b'],
  ['#696eff', '#f8acff'],
  ['#3BCEB8', '#C6F8FF'],
];

const assignRestaurantGradientColor = (i: number) => {
  return gradientColors[i % gradientColors.length];
};

export const getRestaurantGradient = (id: number) => {
  const gradientColor = assignRestaurantGradientColor(id);

  return (
    'linear-gradient(to right, ' +
    gradientColor[0] +
    ', ' +
    gradientColor[1] +
    ')'
  );
};

/**
 * MAP-PIN PATH ONLY. Decision history:
 *  - paire-app#1 R3: the map was the one place allowed to derive a pin
 *    color/face from `restaurant.googleRating` (grey public-opinion pins with
 *    a public-level face), explicitly excluded from the card/badge change.
 *  - 2026-10-04 (CD): reversed. A restaurant with NO personal rating now gets
 *    ONE grey pin with the 🤔 face ("Unsure TasteMatch"), whether or not a
 *    public rating exists, matching the card badge's "Unsure TasteMatch" state. The yellow no-data pin
 *    and the public-level-face grey pins are no longer selected. Listed
 *    restaurants keep the blue signal (grey/blue split + 🤔).
 * The pin keys returned for the Unsure TasteMatch states are registered at runtime in
 * RestaurantMapView's onMapLoad (grey-recolored no-data base art + composited
 * 🤔) — see the Pin Key (MapPinLegend.tsx) for the full state table.
 * Do not add new `justEmoji`/card call sites here — use
 * `getCardCompatibilityIcon`; the only call site is RestaurantMapView.tsx's
 * `justPin=true`.
 */
export const getRatingIconPath = (restaurant: RatableRestaurant, isMenuItemRating = false, justEmoji = false, justPin = false) => {
  const isListed = restaurant.isBookmarked;

  if (!hasPersonalRating(restaurant)) {
    // No personal rating (same predicate as the badges, so a leaked `rating`
    // on a public_consensus/embedding/none score is also "no personal"): ONE "Unsure TasteMatch" state, `googleRating` is deliberately
    // ignored (2026-10-04, CD).
    const notSureKey = `00_notSure${isListed ? 'List' : ''}Pin`;
    if (justEmoji) return notSureEmojiIcon;
    if (justPin) return notSureKey;
    // Neither flag set: return a pin asset, not an emoji (the function's
    // implicit contract). The yellow no-data asset is only a static fallback;
    // the map always passes `justPin`.
    return noDataPin;
  }

  let level: number;
  if (isMenuItemRating) {
    level = getCompatibilityLevelFromMenuRating(restaurant.rating);
  } else {
    level = getCompatibilityLevelFromInternalRating(restaurant.rating);
  }

  const emojiKey = getEmojiKeyFromLevel(level);

  if (justEmoji) return emojiAssets[emojiKey];

  const pinKey = generatePinKey(emojiKey, isListed);

  if (justPin) return pinKey;

  return pinAssets[pinKey] || noCompatPin;
};

export const getCompatibilityEmoji = (restaurant: RatableRestaurant): RatingEmojiProps => {
  // Product decision (paire-app#1 R3): a public/Google rating must NEVER be
  // rendered through the same emoji/color tier system a personal TasteMatch
  // uses — that was the R2 "Est. from Public" attempt, and CD rejected it
  // because the badge still showed the same happy/colorful face a real
  // match would show, so members still read it as "great match for me." No
  // personal rating (`restaurant.rating == null`) now ALWAYS means the flat
  // "not sure" state here, regardless of `restaurant.googleRating`. The
  // public rating itself still displays elsewhere (RestaurantHero's star
  // stat badge — the card-level "Public Opinion" text row was removed per
  // paire-app#96) as a factual number — this function just no longer maps
  // it through a compatibility tier. Map pins follow the same rule since
  // 2026-10-04 (CD) — see getRatingIconPath's doc comment.
  if (!hasPersonalRating(restaurant)) {
    return NOT_SURE_RATING_EMOJI;
  }

  const level = getCompatibilityLevelFromInternalRating(restaurant.rating);
  return RATING_EMOJIS[level];
};

/**
 * Card/badge-safe rating icon lookup (paire-app#1 R3) — the counterpart to
 * `getRatingIconPath`'s map-pin path, for every card/hero-badge call site
 * that only ever needs an emoji `src` string (never a pin key/asset). Unlike
 * `getRatingIconPath`, this NEVER inspects `restaurant.googleRating`: no
 * personal rating always resolves to the "Unsure TasteMatch" 🤔 icon, full stop. Use
 * this (not `getRatingIconPath(..., justEmoji: true)`) for any new
 * card/badge display; reserve `getRatingIconPath` for the map pin path.
 */
export const getCardCompatibilityIcon = (restaurant: RatableRestaurant, isMenuItemRating = false): string => {
  if (!hasPersonalRating(restaurant)) {
    return notSureEmojiIcon;
  }

  const level = isMenuItemRating
    ? getCompatibilityLevelFromMenuRating(restaurant.rating)
    : getCompatibilityLevelFromInternalRating(restaurant.rating);

  const emojiKey = getEmojiKeyFromLevel(level);
  return emojiAssets[emojiKey];
};

/**
 * Inline visually-hidden styles — hides an element from sighted users while
 * keeping it in the accessibility tree for screen readers.
 * Note: @chakra-ui/visually-hidden is not a dependency in this project.
 */
export const visuallyHiddenSx = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  borderWidth: 0,
} as const;

export const getCompatibilityColor = (restaurant: RatableRestaurant): string => {
  // Twin of getCompatibilityBorderColor below (see its comment for the full
  // rationale, paire-app#1 R2): grey here is intentional even when
  // `restaurant.googleRating` would let a fallback level be derived — a
  // colored fill would read as a real personal TasteMatch, which is
  // reserved for `restaurant.rating != null`. Keep this in sync with
  // getCompatibilityBorderColor if that reasoning ever changes.
  if (!hasPersonalRating(restaurant)) {
    return '#a6a6a5';
  }

  const level = getCompatibilityLevelFromInternalRating(restaurant.rating);
  return COMPATIBILITY_LEVELS[level]?.primaryColor;
};

/**
 * Checks if addressParts contains valid data (not "None None" from unfetched details)
 */
export const hasValidAddressParts = (addressParts: { address1: string; address2: string; address3: string } | null | undefined): boolean => {
  if (!addressParts) return false;
  const address1 = addressParts.address1;
  if (!address1 || address1.includes('None') || address1.trim() === '') return false;
  return true;
};

/**
 * Great-circle distance in miles, unrounded. Use this for any comparison
 * against a threshold (the check-in radius gate); `calculateDistance` below
 * rounds to one decimal for display, and a rounded value lets ~240 m read
 * as 0.1 mi and slip under a 0.124 mi (200 m) gate.
 */
export const distanceMilesBetween = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const R = 3959;
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLng = (lng2 - lng1) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

/** Display helper: miles rounded to one decimal place. Compare against
 * thresholds with `distanceMilesBetween`, not this. (The rounding used to
 * divide by 100, returning a tenth of the true distance — found during the
 * paire-app#96 review.) */
export const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number =>
  Math.round(distanceMilesBetween(lat1, lng1, lat2, lng2) * 10) / 10;

/**
 * Helper to convert hex color to RGB values
 */
const hexToRgb = (hex: string): string => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return '0, 0, 0';
  return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`;
};

/**
 * Get compatibility level (0-5) from restaurant rating.
 *
 * Card-only helper (paire-app#1 R3) — not used by the map path (map pins
 * derive their level from the personal rating via `getRatingIconPath`/
 * `generatePinKey`; since 2026-10-04 they no longer read `googleRating`).
 * Feeds `getCompatibilityGlow` and card "TasteMatch" badge logic
 * (RestaurantCard.tsx), both of which are exactly the kind of colorful
 * personal-match-looking visual signal CD's decision prohibits deriving
 * from a public rating — so this no longer falls back to
 * `restaurant.googleRating`. No personal rating always means level 0.
 */
export const getCompatibilityLevel = (restaurant: RatableRestaurant): number => {
  if (!hasPersonalRating(restaurant)) {
    return 0;
  }
  return getCompatibilityLevelFromInternalRating(restaurant.rating);
};

/**
 * Which rule a Surprise Me spin is drawing from:
 * - `taste_match` — enough TasteMatch scores exist; only restaurants with a
 *   High or Highest TasteMatch are eligible.
 * - `public_opinion` — too few TasteMatch scores; restaurants with no
 *   TasteMatch score can qualify on a High or Highest public (Google) rating.
 */
export type SurpriseMePoolMode = 'taste_match' | 'public_opinion';

/**
 * Eligibility check for the "Surprise Me" spin-and-go action — deliberately
 * the one place in the codebase allowed to consult public rating data (map
 * pins stopped doing so on 2026-10-04, CD).
 *
 * This is NOT a display/badge decision, so it is not bound by the paire-app#1
 * rule that TasteMatch badges/emoji must only ever reflect real personal
 * TasteEngine data: Surprise Me doesn't show a compatibility claim to the
 * member, it just picks something to spin to.
 *
 * paire-app#104 item 1 narrowed the bar from Medium+ to **High or Highest**
 * on both scales (`RATING_THRESHOLDS.internal.high` = 51,
 * `RATING_THRESHOLDS.google.high` = 4.5 — the same tier boundaries
 * `COMPATIBILITY_LEVELS` labels "High"/"Highest"), and made the public
 * fallback conditional on `mode` (see `selectSurpriseMePool`).
 *
 * Short-circuits on personal data in BOTH modes: a restaurant with a
 * TasteMatch score is judged by that score alone. A restaurant the member's
 * own TasteEngine has scored below High is never rescued by a good public
 * rating — public rating can only qualify a restaurant with no personal
 * signal, never override one (CD-confirmed, paire-app#1).
 *
 * Uses `hasPersonalRating` (scoreSource-aware) rather than a bare
 * `rating != null` check, so `public_consensus`/`embedding`/`none` results
 * are never treated as TasteMatch-scored.
 *
 * Do NOT route this through `getCompatibilityLevel` — that function is
 * personal-only by design and reinstating a googleRating fallback there
 * would resurrect the exact bug paire-app#1 fixed.
 */
export const isEligibleForSurpriseMe = (
  restaurant: RatableRestaurant,
  mode: SurpriseMePoolMode
): boolean => {
  if (hasPersonalRating(restaurant)) {
    return restaurant.rating >= RATING_THRESHOLDS.internal.high;
  }
  if (mode === 'taste_match') {
    return false;
  }
  return restaurant.googleRating != null && restaurant.googleRating >= RATING_THRESHOLDS.google.high;
};

export interface SurpriseMePool<T extends RatableRestaurant> {
  mode: SurpriseMePoolMode;
  /** The restaurants a spin may land on. Empty means the "No Matches Yet" state. */
  eligible: T[];
  /** How many restaurants in `countScope` carry a TasteMatch score. */
  tasteMatchScoreCount: number;
}

/**
 * Builds a Surprise Me spin pool (paire-app#104 item 1).
 *
 * @param restaurants - The restaurants a spin may draw from (e.g. one city
 *   section of a list, or the search page's filtered results).
 * @param options.countScope - The restaurants to count TasteMatch scores
 *   over when choosing the mode. Defaults to `restaurants`. Callers pass the
 *   wider set the member is looking at (the whole list, or the search
 *   results before client-side filters) so a small section or a narrow
 *   filter doesn't flip a well-profiled member into public-opinion mode.
 */
export const selectSurpriseMePool = <T extends RatableRestaurant>(
  restaurants: T[],
  options: { countScope?: RatableRestaurant[] } = {}
): SurpriseMePool<T> => {
  const countScope = options.countScope ?? restaurants;
  const tasteMatchScoreCount = countScope.filter(hasPersonalRating).length;
  const mode: SurpriseMePoolMode =
    tasteMatchScoreCount >= SURPRISE_ME_MIN_TASTEMATCH_SCORES ? 'taste_match' : 'public_opinion';
  return {
    mode,
    eligible: restaurants.filter((restaurant) => isEligibleForSurpriseMe(restaurant, mode)),
    tasteMatchScoreCount,
  };
};

/**
 * Get a glow box-shadow effect based on restaurant compatibility level
 * Higher compatibility = more intense, larger glow
 */
export const getCompatibilityGlow = (restaurant: RatableRestaurant): string => {
  // No personal rating: keep the glow neutral/minimal rather than deriving
  // it from level 0 (COMPATIBILITY_LEVELS[0] is "None"/red — the worst
  // possible match), which would conflate "no data yet" with "we scored
  // this and you'll hate it." Same conflation NOT_SURE_RATING_EMOJI's
  // introduction fixed for the emoji face (paire-app#1); twin of
  // getCompatibilityColor/getCompatibilityBorderColor's grey early return.
  if (hasNoPersonalRating(restaurant)) {
    return `0 0 12px rgba(${hexToRgb('#a6a6a5')}, 0.15)`;
  }

  const level = getCompatibilityLevel(restaurant);
  const color = COMPATIBILITY_LEVELS[level]?.primaryColor || '#a6a6a5';
  const rgb = hexToRgb(color);

  // Size increases with compatibility level; alpha comes from the mode-aware
  // --tier-glow-alpha-* tokens (design spec §4.1) so light/dark intensity stays in sync.
  const size = 12 + level * 4;

  return `0 0 ${size}px rgba(${rgb}, var(--tier-glow-alpha-${level}))`;
};

/**
 * Get border color for card based on compatibility
 */
export const getCompatibilityBorderColor = (restaurant: RatableRestaurant): string => {
  // If no internal compatibility rating, use gray border (even if falling back to Google rating)
  //
  // KEPT INTENTIONALLY (paire-app#1 R2 decision): the hero pill badge is the
  // only per-card TasteMatch signal now (the redundant card-row text that
  // used to distinguish "Public Opinion" from "TasteMatch" was removed per
  // paire-app#96), so this flat grey border is a second, reinforcing signal
  // that a colored-but-grey-bordered card is NOT a real personal TasteMatch,
  // even when the emoji/color reflects a public rating. Do not "fix" this to
  // a colored border without also reconsidering whether that re-introduces
  // the same public-opinion-as-TasteMatch conflation the wording fix (and
  // now the row removal) resolved.
  if (hasNoPersonalRating(restaurant)) {
    return '#a6a6a5'; // Gray for no compatibility data
  }
  const level = getCompatibilityLevel(restaurant);
  const color = COMPATIBILITY_LEVELS[level]?.primaryColor || '#a6a6a5';
  return color;
};;;
